import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globals } from '../globalfunctions.js';
import PageShell from '../components/ui/PageShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import { apiClient } from '../utils/apiClient';
import './MailBlocklist.css';

const EMPTY_FORM = { list_type: 'blacklist', match_type: 'email', value: '', notes: '' };

function MailBlocklist() {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ruleToRemove, setRuleToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get('/api/mailBlockRules');
      setRules(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching block rules:', err);
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      navigate('/login');
      return;
    }
    fetchRules();
  }, [navigate, fetchRules]);

  const submitRule = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await apiClient.post('/api/mailBlockRules', form);
      setShowAddModal(false);
      setForm(EMPTY_FORM);
      fetchRules();
    } catch (err) {
      setError(err.message || 'Failed to add rule');
    } finally {
      setSaving(false);
    }
  };

  const confirmRemoveRule = async () => {
    setRemoving(true);
    try {
      await apiClient.delete(`/api/mailBlockRules/${ruleToRemove.id}`);
      setRuleToRemove(null);
      fetchRules();
    } catch (err) {
      console.error('Error deleting block rule:', err);
    } finally {
      setRemoving(false);
    }
  };

  const columns = [
    {
      key: 'list_type',
      label: 'List',
      render: (rule) => (
        <span className={`blocklist-badge blocklist-badge-${rule.list_type}`}>
          {rule.list_type === 'whitelist' ? 'Whitelist' : 'Blacklist'}
        </span>
      )
    },
    {
      key: 'match_type',
      label: 'Type',
      render: (rule) => rule.match_type === 'domain' ? 'Domain' : 'Email address'
    },
    { key: 'value', label: 'Value' },
    { key: 'notes', label: 'Notes', render: (rule) => rule.notes || '—' },
    {
      key: 'createdAt',
      label: 'Added',
      render: (rule) => rule.createdAt ? new Date(rule.createdAt).toLocaleDateString('en-GB') : '—'
    },
    {
      key: 'actions',
      label: '',
      render: (rule) => <Button variant="danger" onClick={() => setRuleToRemove(rule)}>Remove</Button>
    }
  ];

  return (
    <PageShell>
      <div className="blocklist-page">
        <div className="blocklist-header">
          <div>
            <h1>Whitelist &amp; Blacklist</h1>
            <p className="blocklist-intro">
              Blacklisted addresses and domains are never turned into tickets - the email is moved
              straight to the junk folder before it reaches the system. A whitelist entry always wins,
              so you can block a whole domain and still let one address through it. Anything not listed
              here is processed as normal.
            </p>
          </div>
          <Button onClick={() => setShowAddModal(true)}>Add Rule</Button>
        </div>

        {loading ? (
          <p aria-live="polite">Loading rules…</p>
        ) : (
          <ResponsiveTable columns={columns} rows={rules} emptyMessage="No rules yet - every sender is processed as normal." />
        )}
      </div>

      <Modal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Rule"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button type="submit" form="new-block-rule-form" disabled={saving}>{saving ? 'Saving…' : 'Add Rule'}</Button>
          </>
        )}
      >
        <form id="new-block-rule-form" onSubmit={submitRule}>
          {error && <div className="blocklist-error">{error}</div>}
          <FormField id="rule-list-type" label="List">
            <select id="rule-list-type" value={form.list_type} onChange={(e) => setForm({ ...form, list_type: e.target.value })}>
              <option value="blacklist">Blacklist (junk it)</option>
              <option value="whitelist">Whitelist (always allow)</option>
            </select>
          </FormField>
          <FormField id="rule-match-type" label="Match">
            <select id="rule-match-type" value={form.match_type} onChange={(e) => setForm({ ...form, match_type: e.target.value })}>
              <option value="email">Exact email address</option>
              <option value="domain">Whole domain</option>
            </select>
          </FormField>
          <FormField id="rule-value" label={form.match_type === 'domain' ? 'Domain' : 'Email address'} required>
            <input
              id="rule-value"
              type="text"
              autoComplete="off"
              spellCheck={false}
              placeholder={form.match_type === 'domain' ? 'example.com' : 'someone@example.com'}
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
            />
          </FormField>
          <FormField id="rule-notes" label="Notes">
            <input id="rule-notes" type="text" placeholder="Optional" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </FormField>
        </form>
      </Modal>

      <Modal
        open={!!ruleToRemove}
        onClose={() => setRuleToRemove(null)}
        title="Remove Rule"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setRuleToRemove(null)} disabled={removing}>Cancel</Button>
            <Button variant="danger" onClick={confirmRemoveRule} disabled={removing}>{removing ? 'Removing…' : 'Remove'}</Button>
          </>
        )}
      >
        <p>Remove the {ruleToRemove?.list_type} rule for <strong>{ruleToRemove?.value}</strong>? Mail from {ruleToRemove?.match_type === 'domain' ? 'that domain' : 'that address'} will go back to being processed as normal.</p>
      </Modal>
    </PageShell>
  );
}

export default MailBlocklist;
